import React, { useEffect, useState } from 'react';
import { Box, Text, Card } from 'rebass/styled-components';
import { useSelector } from 'react-redux';
import { ui, button } from 'util/ui';
import { Notice } from './adminUi';
import CreateUser from './CreateUser';
import Overview from './Overview';
import UsersList from './UsersList';
import UsersManager from './UsersManager';
import ActivityLog from './ActivityLog';
import SearchSettings from './SearchSettings';
import OrgSettings from './OrgSettings';
import { SettingsCard, SaveRow, fieldLabel, textInput } from './SettingsCard';
import useAuth from 'hooks/useAuth';
import { adminFunctionsEnabled } from 'util/adminApi';
import { searchProvider } from 'hooks/useSearch';
import { useFirestore } from 'react-redux-firebase';
import { firestoreCollection } from 'util/constants';
import { DefaultsType } from './types';
import useDefaultFieldsListener from 'hooks/useDefaultFieldsListener';
import useAdminStats from 'hooks/useAdminStats';
import { AppState } from 'store';

type TabKey = 'overview' | 'users' | 'activity' | 'settings';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
  { key: 'activity', label: 'Activity' },
  { key: 'settings', label: 'Settings' },
];

const DEFAULT_FIELDS: { name: keyof DefaultsType; label: string }[] = [
  { name: 'title', label: 'Title' },
  { name: 'projectNumber', label: 'Project number' },
  { name: 'client', label: 'Client' },
  { name: 'agency', label: 'Agency' },
];

const Admin = () => {
  const [tab, setTab] = useState<TabKey>('overview');
  const { stats, loading, error, reload } = useAdminStats();
  const { user } = useSelector((state: AppState) => state.auth);
  const { role, user: authUser } = useAuth();

  const [projectDefaults, setProjectDefaults] = useState<DefaultsType>({
    title: '',
    projectNumber: '',
    client: '',
    agency: '',
  });
  const [isEmpty, setIsEmpty] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const { defaults } = useDefaultFieldsListener();
  const firestore = useFirestore();

  const handleDefaultsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setSaved(null);
      setProjectDefaults((pre) => ({ ...pre, [name]: value }));
    }
  };

  useEffect(() => {
    if (defaults) setProjectDefaults(defaults);
  }, [defaults]);

  const updateDefaultFields = async () => {
    try {
      const collectionRef = firestore.collection(firestoreCollection.projectDefaults);
      const snapshot = await collectionRef.limit(1).get();
      if (!snapshot.empty) {
        await collectionRef.doc(snapshot.docs[0].id).update(projectDefaults);
        setSaved('Saved');
      } else {
        setSaved('No defaults document found — nothing was saved.');
      }
    } catch (error) {
      console.error('Error updating document:', error);
      setSaved(`Save failed: ${error.message}`);
    }
  };

  useEffect(() => {
    setIsEmpty(DEFAULT_FIELDS.some((f) => !projectDefaults[f.name]));
  }, [projectDefaults]);

  return (
    <Box sx={{ maxWidth: 1376, mx: 'auto', px: ['16px', '32px'], pt: '24px', pb: '16px', fontFamily: ui.font }}>
      {/* Title row like the Projects page (UI refresh, 5 Nov 2026). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: '1 1 240px' }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: ui.ink }}>Admin</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: ui.muted }}>
            {stats ? `Figures updated ${stats.loadedAt.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}` : 'Your database at a glance'}
          </p>
        </div>
        <button type="button" className="archy-btn" onClick={reload} disabled={loading} style={{ ...button('plain'), height: 40 }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Tabs: a row of pills that scrolls sideways on a phone instead of breaking words. */}
      <nav aria-label="Admin sections" className="archy-tabs" style={{ marginBottom: 20 }}>
        {TABS.filter((t) => t.key !== 'activity' || adminFunctionsEnabled).map((t) => {
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              className="archy-btn"
              aria-current={on ? 'page' : undefined}
              onClick={() => setTab(t.key)}
              style={{
                flex: 'none',
                height: 38,
                padding: '0 16px',
                borderRadius: 999,
                border: `1px solid ${on ? ui.ink : ui.control}`,
                background: on ? ui.ink : ui.surface,
                color: on ? '#FFFFFF' : ui.text2,
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: on ? 600 : 500,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      {error && <Notice tone="error">Couldn't load the dashboard figures: {error}</Notice>}

      {(tab === 'overview' || (tab === 'users' && !adminFunctionsEnabled)) && !stats && loading && (
        <p style={{ color: ui.muted, fontSize: 14 }}>Loading figures from the database…</p>
      )}

      {tab === 'overview' && stats && <Overview stats={stats} />}
      {tab === 'users' && adminFunctionsEnabled && <UsersManager currentUid={authUser?.uid} currentRole={role} />}
      {tab === 'users' && !adminFunctionsEnabled && stats && <UsersList users={stats.users} currentEmail={user?.email} />}
      {tab === 'activity' && adminFunctionsEnabled && <ActivityLog />}

      {tab === 'settings' && (
        <>
          {adminFunctionsEnabled && <OrgSettings />}
          {/* Same rounded box as the sections above (Aaron, 5 Nov 2026). */}
          <SettingsCard id="defaults-h" title="Project field defaults" note="The labels shown for these fields on every project.">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
              {DEFAULT_FIELDS.map((f) => (
                <label key={f.name} style={fieldLabel}>
                  {f.label}
                  <input style={textInput} onChange={handleDefaultsChange} name={f.name} value={projectDefaults[f.name]} type="text" />
                </label>
              ))}
            </div>
            <SaveRow
              onSave={updateDefaultFields}
              disabled={isEmpty}
              message={saved || (isEmpty ? 'Fill in all four labels to save.' : '')}
            />
          </SettingsCard>

          {adminFunctionsEnabled && searchProvider === 'builtin' && <SearchSettings />}

          {!adminFunctionsEnabled && (
          <Box mb={4}>
            <Card
              p={3}
              mb={2}
              bg="white"
              sx={{ borderLeft: '4px solid', borderColor: 'secondary', borderRadius: 4 }}
            >
              <Text fontSize={1} color="greyDark">
                Adding a user from here is expected to sign you out and into the new account (Firebase signs in
                whoever it creates from the browser). A safer server-side version is planned for phase 2.
              </Text>
            </Card>
            <CreateUser />
          </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Admin;
